import { CHUNK_SIZE } from 'constants';

import { useRef, useState, useEffect } from 'react';

import { UploadOutlined } from '@ant-design/icons';
import { useFormik } from 'formik';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import { resumableSession, resumableUpload } from 'api/endpoints/upload';
import { videoSamples, videoSamplesGetByCategoryId, videoSamplesUpdate } from 'api/endpoints/video-sample';
import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import Input from 'components/Input';
import TitleInput from 'components/TitleInput';
import { MESSAGE_COMMON } from 'constants/string/messages';
import { toastError, toastSuccess } from 'lib/toast';
import { ThumbnailImage, ThumbnailVideo } from 'pages/SampleVideo/CreateSampleVideo/_components/Thumbnail';
import { setLoading } from 'store/actions/common';
import { setModalSampleVideo } from 'store/actions/sample-video';
import {
  getFileExtension,
  getFileName,
  getVideoDurationFromFile,
  getVideoDurationFromVideoUrl,
  toExtendedFile,
  urlToFile,
  validateFileName,
} from 'utils/helper';

import Preview from './_components/Preview';

const MAX_IMAGE_COUNT = 6;
const MAX_VIDEO_COUNT = 1;

const CreateSampleVideo = ({ reloadData }) => {
  const dispatch = useDispatch();
  const { modalSampleVideo } = useSelector((state) => state.sampleVideo);
  const { videoCategories } = useSelector((state) => state.videoCategory);
  const modalRef = useRef(null);
  const [openPreviewMedia, setOpenPreviewMedia] = useState({ isOpen: false, startIndex: 0 });
  const [isSubmited, setIsSubmited] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const initialValues = {
    explain: '',
    video_category_id: null,
  };

  const getSession = async (values) => {
    const dataGetSession = {
      obj_id: modalSampleVideo.isEdit ? modalSampleVideo.id : null,
      video_category_id: values.video_category_id,
      lst_file_name: uploadedFiles.map((file) => file.originalName),
      lst_content_type: uploadedFiles.map((file) => file.type),
      file_custom_urls: uploadedFiles.map((file) => file.name),
    };
    try {
      const res = await resumableSession(dataGetSession);
      if (!res.data.lst_upload_session || res.data.lst_upload_session.length === 0) {
        toastError(MESSAGE_COMMON.FAILURE);
      }

      return res.data.lst_upload_session;
    } catch (err) {
      throw err || { message: MESSAGE_COMMON.FAILURE };
    }
  };

  const handleUploadFileInChunks = async (_file, uploadSession, fileIndex) => {
    const file = _file;
    if (!file) return;

    const uploadFileInChunks = async (fileUpload) => {
      const fileSize = fileUpload.size;
      const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const startByte = chunkIndex * CHUNK_SIZE;
        const endByte = Math.min(startByte + CHUNK_SIZE, fileUpload.size) - 1;
        const chunkBlob = toExtendedFile(
          new File([fileUpload.slice(startByte, endByte + 1)], fileUpload.name, {
            type: fileUpload.type,
          }),
          fileUpload.duration || ''
        );

        const formData = new FormData();
        formData.append('chunk_file', chunkBlob);
        formData.append('upload_session', uploadSession);
        formData.append('start_byte', startByte);
        formData.append('end_byte', endByte);
        formData.append('file_size', fileSize);

        try {
          const res = await resumableUpload(formData);
          if (res.data.complete_status) {
            return res.data.public_url;
          }
        } catch (error) {
          throw error || { message: MESSAGE_COMMON.FAILURE };
        }
      }
    };

    if (file.file.size > CHUNK_SIZE) {
      const publicUrl = await uploadFileInChunks(file.file, uploadSession, fileIndex);
      if (publicUrl) {
        return publicUrl;
      }
    } else {
      const formData = new FormData();
      formData.append('chunk_file', file.file);
      formData.append('upload_session', uploadSession);
      formData.append('start_byte', 0);
      formData.append('end_byte', file.file.size);
      formData.append('file_size', file.file.size);

      const res = await resumableUpload(formData);
      if (res.data.complete_status) {
        return res.data.public_url;
      }
    }
  };

  const form = useFormik({
    initialValues: initialValues,
    validateOnBlur: isSubmited,
    validateOnChange: isSubmited,
    validate: (values) => {
      const errors = {};

      if (!values.video_category_id) {
        errors.video_category_id = '運動カテゴリは必須項目です。入力してください。';
      }

      if (uploadedFiles.length === 0) {
        errors.files = 'ファイルをアップロードしてください';
      }

      for (const [index, file] of uploadedFiles.entries()) {
        if (file.name.trim() === '') {
          errors[`file_name_${index}`] = 'ファイル名は必須です。空欄にできません。';
        } else if (!validateFileName(file.name)) {
          errors[`file_name_${index}`] = `
            使用できない文字が含まれています。<br />
            入力可能文字：英小文字（a-z）、英大文字（A-Z）、ひらがな、カタカナ、漢字、数字（0-9）、<br />
            記号「（）、【】、「」、［］、〜、・、.、-、_、」
          `;
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      try {
        dispatch(setLoading({ isOpen: true }));

        const session = await getSession(values);

        let chunk_files = [];
        for (const [index, signed_url] of session.entries()) {
          const publicUrl = await handleUploadFileInChunks(uploadedFiles[index], signed_url.upload_session, index);
          if (publicUrl) {
            chunk_files.push(publicUrl);
          }
        }

        const fData = new FormData();
        fData.append('video_category_id', values.video_category_id);
        fData.append('explain', values.explain);
        chunk_files.forEach((publicUrl, index) => {
          fData.append('chunk_files', publicUrl);
          fData.append('file_custom_urls', uploadedFiles[index].name);
          fData.append('lst_file_type', uploadedFiles[index].type);
        });

        if (uploadedFiles.length > 0 && uploadedFiles.some((file) => file.type === 'video')) {
          const videoDuration = await getVideoDurationFromVideoUrl(uploadedFiles.find((f) => f.type === 'video').url);
          fData.append('duration', Math.floor(videoDuration));
          const newUrl = URL.createObjectURL(uploadedFiles.find((f) => f.type === 'video').file);
          setUploadedFiles((prev) => prev.map((f) => (f.id === uploadedFiles.find((f) => f.type === 'video').id ? { ...f, url: newUrl } : f)));
        }

        if (modalSampleVideo.isEdit) {
          await videoSamplesUpdate(fData, modalSampleVideo.id)
            .then((res) => {
              toastSuccess(res.message);
              reloadData();
              closeModal();
            })
            .catch((err) => {
              toastError(err.message);
            });
        } else {
          await videoSamples(fData)
            .then((res) => {
              toastSuccess(res.message);
              reloadData();
              closeModal();
            })
            .catch((err) => {
              toastError(err.message);
            });
        }
      } catch (err) {
        toastError(err.message);
      } finally {
        dispatch(setLoading({ isOpen: false }));
      }
    },
  });

  const closeModal = () => {
    uploadedFiles.forEach((file) => {
      if (file.url) {
        URL.revokeObjectURL(file.url);
      }
    });

    setUploadedFiles([]);
    setOpenPreviewMedia({ isOpen: false, startIndex: 0 });
    form.resetForm();

    dispatch(setModalSampleVideo({ isOpen: false }));
  };

  const clickBackdrop = () => {
    modalRef.current.classList.add('animate-fade-out');
    setTimeout(() => {
      modalRef.current.classList.remove('animate-fade-out');
    }, 300);
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newFiles = [];

    for (const file of files) {
      const currentFileType = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : null;

      const uploadedCountImage = uploadedFiles.filter((f) => f.type === 'image').length;
      const currentCountImage = uploadedCountImage + newFiles.filter((f) => f.type === 'image').length;

      const uploadedCountVideo = uploadedFiles.filter((f) => f.type === 'video').length;
      const currentCountVideo = uploadedCountVideo + newFiles.filter((f) => f.type === 'video').length;

      if (currentFileType === 'image' && currentCountImage >= MAX_IMAGE_COUNT) {
        form.setFieldError('files', `写真は最大${MAX_IMAGE_COUNT}枚までアップロードできます`);
        break;
      }

      if (currentFileType === 'video' && currentCountVideo >= MAX_VIDEO_COUNT) {
        form.setFieldError('files', `動画は最大${MAX_VIDEO_COUNT}本までアップロードできます`);
        break;
      }

      if (currentFileType === 'video') {
        const duration = Math.floor(await getVideoDurationFromFile(file));
        if (duration < 3 || duration > 180) {
          form.setFieldError('files', '3秒以上3分以内の動画をアップロードしてください。');
          break;
        }
      }

      form.setFieldError('files', '');

      const fileUrl = URL.createObjectURL(file);
      const newFile = {
        id: Date.now() + Math.random() + newFiles.length,
        file: file,
        url: fileUrl,
        name: file.name.split('.')[0],
        originalName: file.name,
        type: currentFileType,
      };

      newFiles.push(newFile);
    }

    if (newFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    }

    event.target.value = '';
  };

  const removeFile = (fileId) => {
    form.setFieldError('files', '');
    if (isSubmited) {
      const index = uploadedFiles.findIndex((f) => f.id === fileId);
      form.setFieldError(`file_name_${index}`, '');
    }
    setUploadedFiles((prev) => {
      const newFiles = prev.filter((f) => f.id !== fileId);
      return newFiles;
    });
  };

  const updateFileName = (fileId, newName) => {
    setUploadedFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, name: newName } : f)));

    if (isSubmited) {
      const index = uploadedFiles.findIndex((f) => f.id === fileId);
      if (newName.trim() === '') {
        form.setFieldError(`file_name_${index}`, 'ファイル名は必須です。空欄にできません。');
      } else if (!validateFileName(newName)) {
        form.setFieldError(
          `file_name_${index}`,
          `
            使用できない文字が含まれています。<br />
            入力可能文字：英小文字（a-z）、英大文字（A-Z）、ひらがな、カタカナ、漢字、数字（0-9）、<br />
            記号「（）、【】、「」、［］、〜、・、.、-、_、」
          `
        );
      } else {
        form.setFieldError(`file_name_${index}`, '');
      }
    }
  };

  const openPreview = (fileId) => {
    const file = uploadedFiles.find((f) => f.id === fileId);
    if (!file) return;

    const index = uploadedFiles.findIndex((f) => f.id === fileId);
    setOpenPreviewMedia({ isOpen: true, startIndex: index >= 0 ? index : 0 });
  };

  useEffect(() => {
    document.documentElement.classList.add('overflow-hidden');
    document.documentElement.classList.add('h-screen');

    return () => {
      document.documentElement.classList.remove('overflow-hidden');
      document.documentElement.classList.remove('h-screen');
    };
  }, [openPreviewMedia.isOpen]);

  const getSampleVideoByCategoryId = async () => {
    try {
      dispatch(setLoading({ isOpen: true }));
      const res = await videoSamplesGetByCategoryId(modalSampleVideo.id);
      form.setValues({
        explain: res.explain,
        video_category_id: res.id,
      });
      const newFiles = [];

      for (const [index, fileSample] of res.file_samples.entries()) {
        const file = await urlToFile(fileSample.file_url, getFileName(fileSample.file_url), fileSample.file_type);
        newFiles.push({
          id: index,
          file: file,
          url: fileSample.file_url,
          name: fileSample.file_custom_url,
          originalName: `${fileSample.file_custom_url}.${getFileExtension(fileSample.file_url)}`,
          type: fileSample.file_type,
        });
      }
      setUploadedFiles(newFiles);
    } catch (err) {
      dispatch(setLoading({ isOpen: false }));
      toastError(err.message);
    } finally {
      dispatch(setLoading({ isOpen: false }));
    }
  };

  useEffect(() => {
    if (modalSampleVideo.isEdit) {
      getSampleVideoByCategoryId();
    }
  }, [modalSampleVideo.isEdit]);

  return ReactDOM.createPortal(
    <div id="modal" className="fixed inset-0 flex-middle-center bg-gray-800 bg-opacity-50 z-50">
      <div onClick={clickBackdrop} className="fixed inset-0 backdrop" />
      <form ref={modalRef} className="bg-white rounded-sm shadow-lg relative w-191.75 modal-content max-h-[96vh] overflow-y-auto">
        <div className="flex-between-center px-6 py-4 ">
          <p className="font-bold">参考動画・画像の登録</p>
        </div>
        <div className="p-3 relative">
          <div className="absolute top-0 left-0 w-full border-t border-white-720 h-0.25"></div>
          <div className="w-full overflow-y-auto p-3 grid grid-cols-1">
            <div>
              <TitleInput className="mt-0" text={'運動カテゴリ（ユーザに表示される内容）'} required={true} />
              <Input
                id="video_category_id"
                name="video_category_id"
                type="dropdown"
                placeholder="運動カテゴリをプルダウンリストから選択してください。"
                className="w-full"
                value={form.values.video_category_id}
                onChange={(e) => form.handleChange({ target: { name: 'video_category_id', value: e } })}
                aria-invalid={form.errors.video_category_id ? 'true' : 'false'}
                invalid={form.errors.video_category_id}
                options={videoCategories || []}
              />

              <p className="text-sm text-danger-229 max-w-full">
                {form.errors.video_category_id && <ErrorMessage>{form.errors.video_category_id}</ErrorMessage>}
              </p>
            </div>

            <div className="mt-2">
              <TitleInput className="mt-0" text={'参考動画・画像説明'} />
              <Input
                id="explain"
                name="explain"
                type="textarea"
                placeholder="参考動画・画像についての説明を記載してください。"
                className="w-full h-[100px]"
                value={form.values.explain}
                onChange={form.handleChange}
                aria-invalid={form.errors.explain ? 'true' : 'false'}
                invalid={form.errors.explain}
              />
            </div>

            <div className="mt-2">
              <TitleInput className="mt-0 mb-4" text={'運動ファイル名(ユーザ開示)'} required={true} />
              <div className="relative">
                <div className={'h-10 relative w-full flex-middle-center'}>
                  <label htmlFor="fileUpload" className="w-full">
                    <input
                      className={'w-0 h-0 absolute top-0 left-0 -z-max '}
                      id="fileUpload"
                      name="fileUpload"
                      type="file"
                      accept="image/*,video/mp4"
                      multiple
                      onChange={handleFileUpload}
                    />
                    <div
                      className={`w-full h-10 flex justify-center items-center border border-solid ${form.errors.files ? 'border-danger-229' : 'border-gray-300'} cursor-pointer hover:bg-gray-200`}
                    >
                      <UploadOutlined className="mr-2" />
                      <span className="text-sm">
                        {uploadedFiles.length > 0
                          ? `写真(${uploadedFiles.filter((f) => f.type === 'image').length}/${MAX_IMAGE_COUNT})と動画(${uploadedFiles.filter((f) => f.type === 'video').length}/${MAX_VIDEO_COUNT})をアップロード`
                          : `写真（最大${MAX_IMAGE_COUNT}枚）と動画（最大${MAX_VIDEO_COUNT}本）をアップロード`}
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              <p className="text-sm text-danger-229 max-w-full">{form.errors.files && <ErrorMessage>{form.errors.files}</ErrorMessage>}</p>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold mb-4">アップロードされたファイル:</h3>
                <div className="flex flex-col gap-4">
                  {uploadedFiles.map((file, index) => (
                    <div key={file.id} className={'flex items-start gap-3 p-3 w-full  rounded'}>
                      <div className="w-18 h-18 flex-shrink-0">
                        {file.type === 'video' ? (
                          <ThumbnailVideo onClick={() => openPreview(file.id)} onClickRemove={() => removeFile(file.id)} src={file.url} />
                        ) : (
                          <ThumbnailImage onClick={() => openPreview(file.id)} onClickRemove={() => removeFile(file.id)} src={file.url} />
                        )}
                      </div>

                      <div className="flex-1">
                        <Input
                          id={`file_name_${file.id}`}
                          name={`file_name_${file.id}`}
                          type="text"
                          placeholder="ファイル名を表示"
                          className="w-full"
                          value={file.name}
                          invalid={form.errors[`file_name_${index}`]}
                          onChange={(e) => updateFileName(file.id, e.target.value)}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          元のファイル: <span className="font-bold text-black/80">{file.originalName.split('.').slice(0, -1).join('.')}</span>
                        </p>
                        <p className="text-sm text-danger-229 max-w-full">
                          {form.errors[`file_name_${index}`] && (
                            <ErrorMessage>
                              <div dangerouslySetInnerHTML={{ __html: form.errors[`file_name_${index}`] }} />
                            </ErrorMessage>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="h-16 border-t border-white-720 flex-end-center gap-2 py-4 px-6">
          <Button className="h-8 text-sm w-25.5" text={'キャンセル'} color="outline" onClick={closeModal} />
          <Button
            type="button"
            className="h-8 text-sm w-44.5"
            text={modalSampleVideo.isEdit ? '運動動画の更新完了' : '運動動画の入力完了'}
            color="primary"
            onClick={() => {
              setIsSubmited(true);
              form.handleSubmit();
            }}
          />
        </div>
      </form>
      {openPreviewMedia.isOpen && (
        <Preview
          media_files={uploadedFiles}
          close={() => setOpenPreviewMedia({ isOpen: false, startIndex: 0 })}
          firstOpen={openPreviewMedia.startIndex}
        />
      )}
    </div>,
    document.body
  );
};

export default CreateSampleVideo;
